import { useState, useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { X, Send, MessageCircle, Calendar, Tag, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

const DEFAULT_COLUMNS = [
  { id: "urgent-important", name: "Urgent & Important", emoji: "🔴" },
  { id: "followups", name: "Followups with team", emoji: "🔵" },
  { id: "important-regular", name: "Important & Regular", emoji: "🟣" },
  { id: "event-followups", name: "Event Followups", emoji: "🟢" },
];

export default function MemberTasksPanel({ group, member, currentUserEmail, isAdmin = true, onClose }) {
  const [tasks, setTasks] = useState([]);
  const [sections, setSections] = useState([]);
  const [messages, setMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [activeTab, setActiveTab] = useState("tasks");
  const [selectedTask, setSelectedTask] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [commentReads, setCommentReads] = useState({});
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [addingTask, setAddingTask] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (member) {
      fetchMemberTasks();
      fetchChat();
      fetchCommentReads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  // Poll for updates every 5 seconds
  useEffect(() => {
    if (!member) return;
    const interval = setInterval(() => {
      fetchMemberTasks();
      fetchChat();
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  const fetchMemberTasks = async () => {
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/members/${member.member_id}/tasks`, {
        credentials: "include",
      });
      if (res.ok) setTasks(await res.json());
    } catch (err) {
      console.error("Failed to fetch member tasks:", err);
    }
  };

  const fetchCommentReads = async () => {
    try {
      const res = await fetch(`${API}/comment-reads`, { credentials: "include" });
      if (res.ok) setCommentReads(await res.json());
    } catch { /* silent */ }
  };

  const markTaskRead = async (taskId, commentCount) => {
    const readCount = commentReads[taskId] || 0;
    if (commentCount > readCount) {
      setCommentReads(prev => ({ ...prev, [taskId]: commentCount }));
      try {
        await fetch(`${API}/comment-reads/${taskId}`, { method: "POST", credentials: "include" });
      } catch { /* silent */ }
    }
  };

  const fetchChat = async () => {
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/chat/${member.member_id}`, {
        credentials: "include",
      });
      if (res.ok) setMessages(await res.json());
    } catch (err) {
      console.error("Failed to fetch chat:", err);
    }
  };

  const handleSendChat = async () => {
    if (!chatText.trim()) return;
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/chat/${member.member_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: chatText.trim() }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setChatText("");
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    } catch {
      toast.error("Failed to send");
    }
  };

  const handleAddComment = async (taskId) => {
    if (!commentText.trim()) return;
    setSendingComment(true);
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/tasks/${taskId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: commentText.trim() }),
      });
      if (res.ok) {
        const updatedTask = await res.json();
        setTasks(prev => prev.map(t => t.task_id === taskId ? updatedTask : t));
        setSelectedTask(updatedTask);
        setCommentText("");
        toast.success("Comment added");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add comment");
      }
    } catch {
      toast.error("Failed to add comment");
    } finally {
      setSendingComment(false);
    }
  };

  useEffect(() => {
    if (activeTab === "chat") {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, activeTab]);

  const handleAddTaskForMember = async () => {
    const title = newTaskTitle.trim();
    if (!title || addingTask) return;
    setAddingTask(true);
    try {
      const res = await fetch(`${API}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title,
          group_id: group.group_id,
          on_behalf_of_member_id: member.member_id,
          status: "urgent-important",
          priority: "high",
        }),
      });
      if (res.ok) {
        const created = await res.json();
        setTasks(prev => [created, ...prev]);
        setNewTaskTitle("");
        toast.success(`Task added for ${member.name}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add task");
      }
    } catch {
      toast.error("Failed to add task");
    } finally {
      setAddingTask(false);
    }
  };

  // Group tasks by status into columns
  const allColumns = [...DEFAULT_COLUMNS];
  // Collect unique statuses from tasks that aren't default columns
  const defaultIds = new Set(DEFAULT_COLUMNS.map(c => c.id));
  tasks.forEach(t => {
    if (t.status && !defaultIds.has(t.status)) {
      if (!allColumns.find(c => c.id === t.status)) {
        allColumns.push({ id: t.status, name: t.status, emoji: "📋" });
      }
    }
  });

  const getTasksByColumn = (colId) => {
    return tasks.filter(t => {
      if (t.status) return t.status === colId;
      // Fallback: map by priority
      switch (colId) {
        case "urgent-important": return t.priority === "high";
        case "followups": return t.priority === "medium";
        case "important-regular": return t.priority === "low";
        case "event-followups": return !t.priority || t.priority === "none";
        default: return false;
      }
    });
  };

  // Columns that have tasks
  const activeColumns = allColumns.filter(col => getTasksByColumn(col.id).length > 0);
  // Also include default columns even if empty
  const columnsToShow = allColumns.filter(col => 
    defaultIds.has(col.id) || getTasksByColumn(col.id).length > 0
  );

  if (!member) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="member-tasks-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative m-4 flex-1 rounded-xl overflow-hidden flex flex-col dark" data-theme="dark" style={{ backgroundColor: '#1a1d21', color: '#fff' }}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex-shrink-0" style={{ backgroundColor: '#1e2126' }}>
          <div className="flex items-center gap-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium text-white flex-shrink-0"
              style={{ backgroundColor: member.color }}
            >
              {member.initials}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-white truncate">
                {member.name}'s Board
              </h2>
              <p className="text-xs text-gray-500">{member.email} &middot; {member.role} &middot; Read-only view</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-white/5 rounded-lg p-1">
              <button
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "tasks" ? "bg-white/10 text-white" : "text-gray-400 hover:text-gray-300"
                }`}
                onClick={() => setActiveTab("tasks")}
                data-testid="tab-tasks"
              >
                Board ({tasks.length})
              </button>
              <button
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "chat" ? "bg-white/10 text-white" : "text-gray-400 hover:text-gray-300"
                }`}
                onClick={() => setActiveTab("chat")}
                data-testid="tab-chat"
              >
                Chat ({messages.length})
              </button>
            </div>

            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg" data-testid="member-panel-close">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Tasks Tab - Kanban Board */}
        {activeTab === "tasks" && (
          <div className="flex-1 overflow-x-auto overflow-y-hidden p-4 flex flex-col">
            {/* Add task on behalf of member (admins/EA only) */}
            {isAdmin && (
              <div className="mb-3 flex-shrink-0">
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 max-w-md">
                  <span className="text-xs text-gray-400 flex-shrink-0">+ Add task for {member.name}:</span>
                  <input
                    data-testid="admin-add-task-for-member"
                    type="text"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    placeholder="Task title…"
                    className="flex-1 bg-transparent text-white placeholder:text-gray-500 outline-none text-sm"
                    onKeyDown={(e) => { if (e.key === "Enter") handleAddTaskForMember(); }}
                  />
                  {newTaskTitle.trim() && (
                    <button
                      onClick={handleAddTaskForMember}
                      disabled={addingTask}
                      className="px-2.5 py-1 bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white text-xs rounded transition-colors"
                      data-testid="admin-add-task-for-member-btn"
                    >
                      Add
                    </button>
                  )}
                </div>
              </div>
            )}
            {tasks.length === 0 ? (
              <div className="flex items-center justify-center flex-1">
                <div className="text-center">
                  <CheckCircle2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">No tasks for {member.name}</p>
                </div>
              </div>
            ) : (
              <div className="flex gap-4 flex-1 min-h-0">
                {columnsToShow.map(col => {
                  const colTasks = getTasksByColumn(col.id);
                  return (
                    <div key={col.id} className="flex-shrink-0 w-72 flex flex-col rounded-xl" style={{ backgroundColor: '#23272b' }}>
                      {/* Column Header */}
                      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                        <span className="text-base">{col.emoji}</span>
                        <h3 className="text-sm font-semibold text-white truncate">{col.name}</h3>
                        <span className="ml-auto text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">{colTasks.length}</span>
                      </div>

                      {/* Tasks */}
                      <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        {colTasks.map(task => {
                          const totalComments = task.comments?.length || 0;
                          const readCount = commentReads[task.task_id] || 0;
                          const unread = Math.max(0, totalComments - readCount);
                          const isSelected = selectedTask?.task_id === task.task_id;

                          return (
                            <div
                              key={task.task_id}
                              className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                                isSelected
                                  ? "border-blue-500/50 bg-blue-500/10"
                                  : "border-white/5 bg-white/5 hover:bg-white/[0.08]"
                              }`}
                              onClick={() => {
                                const opening = !isSelected;
                                setSelectedTask(opening ? task : null);
                                setCommentText("");
                                if (opening && unread > 0) markTaskRead(task.task_id, totalComments);
                              }}
                              data-testid={`member-task-${task.task_id}`}
                            >
                              {/* Tags */}
                              {task.tags?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {task.tags.map((tag, i) => (
                                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">{tag}</span>
                                  ))}
                                </div>
                              )}

                              {/* Task Title Row */}
                              <div className="flex items-start gap-2">
                                <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                                  task.completed ? "border-green-500 bg-green-500" : "border-gray-500"
                                }`}>
                                  {task.completed && (
                                    <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none">
                                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${task.completed ? "text-gray-500 line-through" : "text-white"}`}>
                                    {task.title || "Untitled"}
                                  </p>
                                  {task.description && (
                                    <p className="text-xs text-gray-500 mt-1 truncate">{task.description}</p>
                                  )}
                                </div>
                                {unread > 0 && (
                                  <div className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                                    <span className="text-[9px] font-bold text-white">{unread}</span>
                                  </div>
                                )}
                              </div>

                              {/* Bottom row: due date + comment count */}
                              <div className="flex items-center gap-3 mt-2 flex-wrap">
                                {task.due_date && (
                                  <div className="flex items-center gap-1 text-[10px] text-gray-500">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(task.due_date).toLocaleDateString([], { month: "short", day: "numeric" })}
                                  </div>
                                )}
                                {totalComments > 0 && (
                                  <div className="flex items-center gap-1 text-[10px] text-gray-500">
                                    <MessageCircle className="w-3 h-3" />
                                    {totalComments}
                                  </div>
                                )}
                                {task.added_by_email && task.added_by_email !== member.email && (
                                  <span
                                    className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/20"
                                    title={`Added by ${task.added_by_name || task.added_by_email}`}
                                    data-testid={`task-added-by-${task.task_id}`}
                                  >
                                    Added by {task.added_by_name || task.added_by_email.split("@")[0]}
                                  </span>
                                )}
                                {task.last_edited_by_email && (
                                  <span
                                    className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/20"
                                    title={`Last edited by ${task.last_edited_by_name || task.last_edited_by_email}`}
                                    data-testid={`task-edited-by-${task.task_id}`}
                                  >
                                    Edited by {task.last_edited_by_name || task.last_edited_by_email.split("@")[0]}
                                  </span>
                                )}
                              </div>

                              {/* Expanded: Comments (read-only except reply) */}
                              {isSelected && (
                                <div className="mt-3 pt-3 border-t border-white/10 space-y-2" onClick={(e) => e.stopPropagation()}>
                                  {task.comments?.length > 0 && (
                                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                      {task.comments.map((c) => {
                                        const isAdmin = c.user_name !== member?.name;
                                        return (
                                          <div key={c.id} className={`text-xs rounded p-2 ${isAdmin ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-blue-500/10 border border-blue-500/20'}`}>
                                            <div className="flex items-center gap-1.5 mb-1">
                                              <span className={`font-medium ${isAdmin ? 'text-emerald-400' : 'text-blue-400'}`}>{c.user_name || "User"}</span>
                                              <span className="text-gray-600 text-[10px]">
                                                {new Date(c.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                              </span>
                                            </div>
                                            <span className="text-gray-300">{c.text}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {/* Admin comment input (admins only) */}
                                  {isAdmin && (
                                  <div className="flex gap-2 mt-2">
                                    <Input
                                      data-testid={`task-comment-input-${task.task_id}`}
                                      value={commentText}
                                      onChange={(e) => setCommentText(e.target.value)}
                                      placeholder="Add a comment..."
                                      className="text-xs h-8"
                                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#fff' }}
                                      onKeyDown={(e) => { if (e.key === "Enter") handleAddComment(task.task_id); }}
                                    />
                                    <button
                                      data-testid={`task-comment-send-${task.task_id}`}
                                      onClick={() => handleAddComment(task.task_id)}
                                      disabled={sendingComment || !commentText.trim()}
                                      className="p-1.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-40 rounded-lg transition-colors flex-shrink-0"
                                    >
                                      <Send className="w-3.5 h-3.5 text-white" />
                                    </button>
                                  </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === "chat" && (
          <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full">
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 && (
                <div className="text-center py-10">
                  <MessageCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No messages yet</p>
                </div>
              )}
              {messages.map((msg) => {
                const isSelf = msg.sender_email === currentUserEmail;
                return (
                  <div key={msg.msg_id} className={`flex mb-3 ${isSelf ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[75%]">
                      {!isSelf && (
                        <div className="flex items-center gap-2 mb-1">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-medium text-white"
                            style={{ backgroundColor: msg.sender_color || "#6b7280" }}
                          >
                            {msg.sender_initials}
                          </div>
                          <span className="text-xs text-gray-500">{msg.sender_name}</span>
                        </div>
                      )}
                      <div className={`px-3 py-2 rounded-xl text-sm ${
                        isSelf
                          ? "bg-blue-500 text-white rounded-br-sm"
                          : "bg-white/10 text-white rounded-bl-sm"
                      }`}>
                        {msg.text}
                      </div>
                      <p className="text-[10px] text-gray-600 mt-0.5 px-1">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </ScrollArea>

            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  data-testid="admin-chat-input"
                  value={chatText}
                  onChange={(e) => setChatText(e.target.value)}
                  placeholder="Reply to member..."
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#fff' }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSendChat(); }}
                />
                <button
                  data-testid="admin-chat-send"
                  onClick={handleSendChat}
                  className="p-2.5 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
